import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import MapView from "./MapView";

interface MarkerType {
  lat: number;
  lng: number;
  label: string;
  isInRange: boolean;
  floristId?: string;
}

interface FloristData {
  id: string;
  store_name: string;
  suburb?: string;
  state?: string;
  coordinates?: any;
  geocoded_address?: any;
}

export function FloristsMap() {
  const [markers, setMarkers] = useState<MarkerType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFlorists();
  }, []);

  const fetchFlorists = async () => {
    try {
      setLoading(true);
      
      // Fetch all active florists with location data
      const { data: florists, error } = await supabase
        .from('store_profiles')
        .select('id, store_name, suburb, state, coordinates, geocoded_address')
        .eq('store_status', 'active')
        .not('coordinates', 'is', null)
        .not('geocoded_address', 'is', null);

      if (error) {
        console.error('Error fetching florists for map:', error);
        return;
      }

      // Convert florists to map markers
      const mapMarkers: MarkerType[] = [];
      
      florists?.forEach((florist: FloristData) => {
        let coords = null;
        
        // Try to get coordinates from the coordinates field first
        if (florist.coordinates && typeof florist.coordinates === 'object' && 'lat' in florist.coordinates && 'lng' in florist.coordinates) {
          coords = { lat: florist.coordinates.lat, lng: florist.coordinates.lng };
        } 
        // Fallback to geocoded_address
        else if (florist.geocoded_address) {
          try {
            const parsedGeocode = typeof florist.geocoded_address === 'string' 
              ? JSON.parse(florist.geocoded_address) 
              : florist.geocoded_address;
              
            if (parsedGeocode?.geometry?.location) {
              coords = { 
                lat: parsedGeocode.geometry.location.lat, 
                lng: parsedGeocode.geometry.location.lng 
              };
            } else if (parsedGeocode?.lat && parsedGeocode?.lng) {
              coords = { lat: parsedGeocode.lat, lng: parsedGeocode.lng };
            }
          } catch (error) {
            console.error('Error parsing geocoded_address for florist:', florist.id, error);
          }
        }

        if (coords) {
          const locationText = florist.suburb && florist.state 
            ? `${florist.suburb}, ${florist.state}` 
            : '';
            
          mapMarkers.push({
            lat: coords.lat,
            lng: coords.lng,
            label: `${florist.store_name}${locationText ? ` (${locationText})` : ''}`,
            isInRange: true,
            floristId: florist.id
          });
        }
      });

      setMarkers(mapMarkers);
    } catch (error) {
      console.error('Error in fetchFlorists:', error);
    } finally {
      setLoading(false);
    }
  };

  // For now, we'll default to Sydney. Later we can add user preference or use a CORS-friendly service
  const defaultCenter = { lat: -33.8688, lng: 151.2093 };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#f8ecd4] border border-brand-primary rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <MapView 
      markers={markers}
      searchedLocation={defaultCenter}
      allFlorists={markers}
    />
  );
} 